// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
The listings that sell fastest are not the ones that sound the best. They are the ones that answer every question a careful buyer was about to ask, before they have to ask it. A buyer who has to send three messages to find out the ring size usually does not send the first one.

## The title carries most of the work

A title has one job: to match what someone typed into a search box. So write the words a buyer would use, not the words a poet would.

Put these in, in roughly this order:

1. **What it is.** Ring, brooch, pendant, Albert chain.
2. **The metal and purity.** 18K gold, sterling silver, platinum.
3. **The main stone**, if there is one.
4. **Era or style**, if you are confident. Victorian, Art Deco, mid century.
5. **The size**, for rings.

**Weak:** A beautiful sparkling treasure from a bygone age

**Strong:** Art Deco 18K gold ring, old European cut diamond, size 54 / US 7

Leave out subjective adjectives. Stunning, gorgeous and rare tell a buyer nothing and are in every competing listing anyway. Save them for the description, if at all.

## The first three lines of the description

Assume the buyer reads only these. Put the facts that decide a purchase there:

- What it is and what it is made of.
- The measurements that matter, with numbers.
- The condition in one honest sentence.

Everything else, the history, the story, the styling suggestion, goes below.

## The measurements that actually matter

Different pieces need different numbers, and the wrong ones are useless.

| Piece | Give these |
| --- | --- |
| Ring | Inside diameter in mm, plus the size in your local system, plus band width |
| Necklace or chain | Length flat from clasp end to clasp end, plus weight |
| Bracelet | Internal circumference, plus width |
| Earrings | Length including fitting, width, weight per earring |
| Brooch | Height and width, plus weight |
| Pendant | Height including bail, width |
| Any piece with stones | Approximate stone dimensions in mm |

Give the **weight in grams** on anything gold or silver. It lets a buyer work out the metal value themselves, which builds trust, and it also tells them whether a chain is solid or hollow. Our [resale guide](/blog/how-much-is-my-jewelry-worth) explains that calculation.

For rings, inside diameter in millimetres removes all confusion between US, UK and European sizing. Our [ring size guide](/blog/how-to-measure-your-ring-size-at-home) covers how to measure it.

## Describe the flaws first, not last

This feels wrong and it works.

A listing that says "small chip to the enamel at the lower left of the frame, shown in photograph 4" does three things at once: it answers the question, it proves you looked carefully, and it makes every other claim in the listing more believable.

Buyers of second hand jewelry expect wear. What they will not forgive is discovering something you did not mention. Almost every not-as-described dispute is really a not-disclosed dispute, as our [guide to selling safely](/blog/how-to-sell-jewelry-safely-online) explains.

List, plainly:

- Scratches, dents and worn engraving
- Chips or cracks in stones or enamel
- Replaced or mismatched stones
- Repairs, resizes and replaced clasps or fittings
- Missing parts, including original boxes
- Anything that no longer works: a stiff clasp, a worn screw thread

## Say what you know, and say what you do not

You do not need to be an expert to write a good listing. You need to be clear about which is which.

**Good:** "Marked 585. Unmarked as to maker. I believe 1930s from the setting, but this is my opinion, not a formal attribution. Not laboratory tested."

**Risky:** "Genuine 1930s Art Deco diamond ring, certified."

The first invites a knowledgeable buyer to make up their own mind and protects you if they disagree. The second is a promise you may not be able to keep, and it is the sentence a dispute gets argued over.

If you are describing gemstones, be careful with the word natural. Our [gemstone guide](/blog/how-to-tell-if-a-gemstone-is-real) covers the difference between natural, synthetic and simulant, and why "created" must be stated.

## Photographs are part of the description

Six shots do the job: the full piece, an angle, the back, a close macro of the hallmark, something for scale, and an honest photograph of every flaw you listed.

Daylight, no flash, plain background, exposure pulled down so the metal does not blow out. Our [phone photography guide](/blog/how-to-photograph-jewelry-with-your-phone) has the settings.

Two rules that matter more than technique:

- **Photograph the actual piece.** Never use the previous seller's images or a stock photo. It is the fastest way to lose a buyer's trust, and it will not match what arrives.
- **Do not retouch beyond cropping, straightening and white balance.** Buyers are reading the photographs for evidence of condition. Removing a scratch in software removes the evidence and creates a dispute.

## Price it where it will sell

Work backwards. Decide what you need in your bank account, then add the commission on top, then check that number against what comparable pieces have actually sold for, not what they are listed at.

Leave a little room. Buyers on Nextmine can send an offer, and many sellers price around ten percent above their real target so an accepted offer still lands where they wanted.

## Shipping, stated plainly

Say where you ship from, what it costs, and how long it takes. If you ship free to some countries and not others, say which. If import duties are the buyer's responsibility, say so in the listing rather than leaving it as a surprise on the doorstep.

## A checklist before you publish

- Title contains the object, the metal, the stone and the size
- First three lines answer what, how big, what condition
- Weight in grams given
- Every measurement a buyer needs, in millimetres
- Every flaw listed, and photographed
- Marks photographed and transcribed
- What you know separated from what you believe
- Six photographs, daylight, unretouched
- Shipping and duties stated

Ready to list? [Start here](/signup), or read [how payments are protected](/how-payments-work) first. And if you are still deciding where to sell at all, our [honest comparison of the options](/blog/where-to-sell-pre-loved-fine-jewelry-online) goes through each route with its fees.
`;

export default body;
